import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { parseApiError } from '../../../../utils/errorUtils';
import DataGrid, { GridColumn, GridAction } from '../../../../components/DataGrid/DataGrid';
import {
  salesInvoiceService,
  SalesInvoiceListDTO,
  OutstandingInvoiceSummaryDTO,
} from '../../../../services/salesInvoiceService';
import { customerService } from '../../../../services/customerService';
import './CustomerOutstandingReviewPage.css';
import { AlarmClock, CircleDollarSign, ClockFading, ReceiptText, RefreshCw, ArrowLeft, Send, Eye, Check, AlertCircle } from 'lucide-react';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface Customer { id: number; name: string; }
type StatusFilter = 'all' | 'pending' | 'overdue';

// ─── Component ─────────────────────────────────────────────────────────────────

const CustomerOutstandingReviewPage: React.FC = () => {
  const navigate  = useNavigate();
  const { t, i18n } = useTranslation();

  // ── Data ──────────────────────────────────────────────────────────────────────
  const [invoices,   setInvoices]   = useState<SalesInvoiceListDTO[]>([]);
  const [customers,  setCustomers]  = useState<Customer[]>([]);
  const [summary,    setSummary]    = useState<OutstandingInvoiceSummaryDTO | null>(null);

  // ── Loading ───────────────────────────────────────────────────────────────────
  const [loading,        setLoading]        = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [settling,       setSettling]       = useState(false);

  // ── Feedback ──────────────────────────────────────────────────────────────────
  const [successMsg,  setSuccessMsg]  = useState<string | null>(null);
  const [settleError, setSettleError] = useState<string | null>(null);

  // ── Filters ───────────────────────────────────────────────────────────────────
  const [customerFilter, setCustomerFilter] = useState<number | 'all'>('all');
  const [statusFilter,   setStatusFilter]   = useState<StatusFilter>('all');

  // ── Settlement modal ──────────────────────────────────────────────────────────
  const [selectedInvoice, setSelectedInvoice] = useState<SalesInvoiceListDTO | null>(null);
  const [showModal,        setShowModal]        = useState(false);
  const [settlementDate,   setSettlementDate]   = useState(new Date().toISOString().split('T')[0]);
  const [remarks,          setRemarks]          = useState('');

  const locale = i18n.language === 'ja' ? 'ja-JP' : 'en-US';

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const fmt = (n: number) =>
    n.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fmtDate = (d: string) => new Date(d).toLocaleDateString(locale);

  // ─── Load customers (once) ───────────────────────────────────────────────────

  useEffect(() => {
    customerService.getCustomers({ pageSize: 1000 }).then(res => {
      const data = res.data?.data;
      if (Array.isArray(data))
        setCustomers(data.map(c => ({ id: c.id, name: c.name || `Customer ${c.id}` })));
    }).catch(() => {});
  }, []);

  // ─── Load summary ────────────────────────────────────────────────────────────

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await salesInvoiceService.getOutstandingSummary(
        customerFilter !== 'all' ? customerFilter : undefined
      );
      setSummary(res.data?.data ?? null);
    } catch {
      // non-critical
    } finally {
      setSummaryLoading(false);
    }
  }, [customerFilter]);

  // ─── Load invoice list ───────────────────────────────────────────────────────

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { pageNumber: 1, pageSize: 500 };
      if (customerFilter !== 'all') params.customerId = customerFilter;
      const res = await salesInvoiceService.getOutstandingInvoices(params as any);
      const data = res.data?.data ?? [];
      setInvoices(Array.isArray(data) ? data : []);
    } catch {
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [customerFilter]);

  useEffect(() => {
    loadSummary();
    loadInvoices();
  }, [loadSummary, loadInvoices]);

  // ─── Auto-dismiss success ────────────────────────────────────────────────────

  useEffect(() => {
    if (!successMsg) return;
    const timer = setTimeout(() => setSuccessMsg(null), 5000);
    return () => clearTimeout(timer);
  }, [successMsg]);

  // ─── Filtered data for DataGrid ──────────────────────────────────────────────

  const filteredInvoices = useMemo(() => {
    if (statusFilter === 'overdue') return invoices.filter(i => i.isOverdue);
    if (statusFilter === 'pending') return invoices.filter(i => !i.isOverdue);
    return invoices;
  }, [invoices, statusFilter]);

  // ─── Settlement handlers ─────────────────────────────────────────────────────

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const openModal = (inv: SalesInvoiceListDTO) => {
    setSelectedInvoice(inv);
    setSettlementDate(new Date().toISOString().split('T')[0]);
    setRemarks('');
    setSettleError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    if (!settling) setShowModal(false);
  };

  const handleSettle = async () => {
    if (!selectedInvoice || !settlementDate) return;
    setSettling(true);
    setSettleError(null);
    try {
      await salesInvoiceService.settleCreditInvoice(selectedInvoice.id, {
        settlementDate,
        remarks: remarks.trim() || undefined,
      });
      setShowModal(false);
      setSuccessMsg(
        t('customerOutstanding.settledSuccess', { invoice: selectedInvoice.invoiceNumber })
      );
      await Promise.all([loadSummary(), loadInvoices()]);
    } catch (err: unknown) {
      setSettleError(parseApiError(err, t('customerOutstanding.failedToSettle')).message);
    } finally {
      setSettling(false);
    }
  };

  // ─── DataGrid columns ────────────────────────────────────────────────────────

  const columns: GridColumn[] = [
    {
      key: 'invoiceNumber',
      label: t('customerOutstanding.invoiceNo'),
      sortable: true,
      width: '140px',
      render: (value, item) => (
        <button
          className="cor-invoice-link"
          onClick={() => navigate(`/sales/sales-invoices/${item.id}`)}
        >
          {value}
        </button>
      ),
    },
    {
      key: 'customerName',
      label: t('customerOutstanding.customerName'),
      sortable: true,
      render: (_v, item) => (
        <span className="cor-customer-name">{item.customerName}</span>
      ),
    },
    {
      key: 'invoiceDate',
      label: t('customerOutstanding.invoiceDate'),
      sortable: true,
      width: '120px',
      hideMobile: true,
      render: (v) => fmtDate(v),
    },
    {
      key: 'dueDate',
      label: t('customerOutstanding.dueDate'),
      sortable: true,
      width: '140px',
      render: (v, item) => (
        <span className={item.isOverdue ? 'cor-due-overdue' : ''}>
          {fmtDate(v)}
          {item.isOverdue && <span className="cor-overdue-tag">!</span>}
        </span>
      ),
    },
    {
      key: 'totalAmount',
      label: t('customerOutstanding.totalAmount'),
      sortable: true,
      width: '130px',
      hideMobile: true,
      hideTablet: true,
      render: (v) => <span className="cor-amount">{fmt(v)}</span>,
    },
    {
      key: 'balanceDue',
      label: t('customerOutstanding.balanceDue'),
      sortable: true,
      width: '130px',
      render: (v) => <span className="cor-balance">{fmt(v)}</span>,
    },
    {
      key: 'status',
      label: t('salesInvoice.status') || 'Invoice Status',
      width: '110px',
      hideMobile: true,
      render: (v) => {
        switch (v) {
          case 'Draft':         return <span className="ims-badge ims-badge-draft">Draft</span>;
          case 'Issued':        return <span className="ims-badge ims-badge-confirmed">Issued</span>;
          case 'PartiallyPaid': return <span className="ims-badge ims-badge-partially-received">Partial</span>;
          case 'Overdue':       return <span className="ims-badge ims-badge-rejected">Overdue</span>;
          default:              return <span className="ims-badge ims-badge-draft">{v}</span>;
        }
      },
    },
    {
      key: 'isOverdue',
      label: t('customerOutstanding.status'),
      width: '110px',
      render: (_v, item) =>
        item.isOverdue ? (
          <span className="ims-badge ims-badge-rejected">
            {t('customerOutstanding.overdue')}
          </span>
        ) : (
          <span className="ims-badge ims-badge-pending">
            {t('customerOutstanding.pending')}
          </span>
        ),
    },
  ];

  const actions: GridAction[] = [
    {
      label: t('salesInvoice.issue') || 'Issue',
      icon: (
        <Send size={14} strokeWidth={2} />
      ),
      onClick: async (item) => {
        try {
          await salesInvoiceService.issueSalesInvoice(item.id);
          await Promise.all([loadSummary(), loadInvoices()]);
        } catch (err: unknown) {
          alert(parseApiError(err, 'Failed to issue invoice').message);
        }
      },
      className: 'grid-action-btn-edit',
      title: t('salesInvoice.issue') || 'Issue Invoice',
      // Only show Issue button for Draft invoices
      hidden: (item) => item.status !== 'Draft',
    },
    {
      label: t('common.view'),
      icon: (
        <Eye size={14} strokeWidth={2} />
      ),
      onClick: (item) => navigate(`/sales/sales-invoices/${item.id}`),
      className: 'grid-action-btn-edit',
      title: t('common.view'),
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="ims-page cor-page">

        {/* ── Hero header ── */}
        <div className="cor-hero">
          <div className="cor-hero-left">
            <button
              className="cor-hero-back-btn"
              onClick={() => navigate('/sales/sales-invoices')}
            >
              <ArrowLeft size={14} strokeWidth={2} />
              {t('customerOutstanding.backToInvoices')}
            </button>
            <div>
              <h1 className="cor-hero-title">
                <ClockFading size={22} strokeWidth={1.8} style={{ verticalAlign: 'middle', marginInlineEnd: 8 }} />
                {t('customerOutstanding.title')}
              </h1>
              <p className="cor-hero-sub">{t('customerOutstanding.subtitle')}</p>
            </div>
          </div>
          <div className="cor-hero-actions">
            <button
              className="cor-hero-refresh-btn"
              onClick={() => { loadSummary(); loadInvoices(); }}
              disabled={loading}
            >
              <RefreshCw size={14} className={loading ? 'cor-spin' : ''} strokeWidth={2} />
              {t('common.refresh')}
            </button>
            <button
              className="cor-hero-action-btn"
              onClick={() => navigate('/sales/sales-invoices')}
            >
              <ReceiptText size={14} strokeWidth={2} />
              {t('customerOutstanding.allInvoices') || 'All Invoices'}
            </button>
          </div>
        </div>

        {/* ── Success banner ── */}
        {successMsg && (
          <div className="ims-alert ims-alert-success cor-success-banner">
            <Check size={16} strokeWidth={2} />
            {successMsg}
          </div>
        )}

        {/* ── Stats row ── */}
        <div className="ims-stats-row cor-stats">
          {/* Total outstanding */}
          <div className="ims-stat-card cor-stat-primary">
            <div className="ims-stat-icon cor-stat-icon-primary">
              <ReceiptText size={20} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">
                {summaryLoading ? '—' : (summary?.totalCount ?? 0)}
              </span>
              <span className="ims-stat-label">{t('customerOutstanding.totalOutstanding')}</span>
              <span className="cor-stat-sub">
                {summaryLoading ? '—' : fmt(summary?.totalBalanceDue ?? 0)}
              </span>
            </div>
          </div>

          {/* Overdue */}
          <div className="ims-stat-card cor-stat-danger">
            <div className="ims-stat-icon cor-stat-icon-danger">
              <AlarmClock size={20} strokeWidth={2} color="red" />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value cor-stat-value-danger">
                {summaryLoading ? '—' : (summary?.overdueCount ?? 0)}
              </span>
              <span className="ims-stat-label">{t('customerOutstanding.overdueInvoices')}</span>
              <span className="cor-stat-sub cor-stat-sub-danger">
                {summaryLoading ? '—' : fmt(summary?.overdueBalanceDue ?? 0)}
              </span>
            </div>
          </div>

          {/* Pending */}
          <div className="ims-stat-card cor-stat-warning">
            <div className="ims-stat-icon cor-stat-icon-warning">
              <ClockFading size={20} strokeWidth={2} color="orange" />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value cor-stat-value-warning">
                {summaryLoading ? '—' : (summary?.pendingCount ?? 0)}
              </span>
              <span className="ims-stat-label">{t('customerOutstanding.pendingInvoices')}</span>
              <span className="cor-stat-sub">
                {summaryLoading ? '—' : fmt(summary?.pendingBalanceDue ?? 0)}
              </span>
            </div>
          </div>

          {/* Total balance due */}
          <div className="ims-stat-card cor-stat-info">
            <div className="ims-stat-icon cor-stat-icon-info">
              <CircleDollarSign size={20} strokeWidth={1.75} color="blue" />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value cor-stat-value-info" style={{ fontSize: '18px' }}>
                {summaryLoading ? '—' : fmt(summary?.totalBalanceDue ?? 0)}
              </span>
              <span className="ims-stat-label">{t('customerOutstanding.totalBalanceDue')}</span>
            </div>
          </div>
        </div>

        {/* ── Filter bar ── */}
        <div className="ims-card cor-filter-card">
          <div className="cor-filter-bar">
            {/* Customer */}
            <div className="cor-filter-group">
              <label className="cor-filter-label">{t('common.customer')}</label>
              <select
                className="cor-select"
                value={customerFilter}
                onChange={e => {
                  setCustomerFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value));
                }}
              >
                <option value="all">{t('customerOutstanding.allCustomers')}</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Status pills */}
            <div className="cor-filter-group">
              <label className="cor-filter-label">{t('common.status')}</label>
              <div className="cor-pills">
                {(['all', 'pending', 'overdue'] as StatusFilter[]).map(s => (
                  <button
                    key={s}
                    className={[
                      'cor-pill',
                      statusFilter === s ? `cor-pill--active cor-pill--${s}` : '',
                    ].join(' ')}
                    onClick={() => setStatusFilter(s)}
                  >
                    {s === 'all'
                      ? t('common.all')
                      : s === 'pending'
                      ? t('customerOutstanding.pending')
                      : t('customerOutstanding.overdue')}
                    {/* show counts */}
                    {!summaryLoading && summary && (
                      <span className="cor-pill-count">
                        {s === 'all'
                          ? summary.totalCount
                          : s === 'pending'
                          ? summary.pendingCount
                          : summary.overdueCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Clear */}
            {(customerFilter !== 'all' || statusFilter !== 'all') && (
              <div className="cor-filter-group cor-filter-clear">
                <label className="cor-filter-label">&nbsp;</label>
                <button
                  className="cor-clear-btn"
                  onClick={() => { setCustomerFilter('all'); setStatusFilter('all'); }}
                >
                  {t('customerOutstanding.clearFilters')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Data table ── */}
        <div className="cor-grid-card">
          <DataGrid
            data={filteredInvoices}
            columns={columns}
            actions={actions}
            loading={loading}
            pageSize={15}
            rowsPerPageOptions={[10, 15, 25, 50]}
            searchPlaceholder={t('customerOutstanding.searchPlaceholder')}
            emptyMessage={t('customerOutstanding.noOutstanding')}
          />
        </div>

      </div>

      {/* ── Settlement modal ── */}
      {showModal && selectedInvoice && (
        <div className="cor-modal-overlay" onClick={closeModal}>
          <div className="cor-modal" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="cor-modal-header">
              <div className="cor-modal-header-icon">
                <Check size={22} strokeWidth={2} />
              </div>
              <div className="cor-modal-header-text">
                <h2 className="cor-modal-title">{t('customerOutstanding.settleInvoice')}</h2>
                <p className="cor-modal-subtitle">{selectedInvoice.invoiceNumber}</p>
              </div>
              <button className="cor-modal-close" onClick={closeModal} disabled={settling}>×</button>
            </div>

            {/* Body */}
            <div className="cor-modal-body">

              {/* Invoice summary */}
              <div className="cor-settle-summary">
                <div className="cor-settle-row">
                  <span className="cor-settle-label">{t('customerOutstanding.customerName')}</span>
                  <span className="cor-settle-value">{selectedInvoice.customerName}</span>
                </div>
                <div className="cor-settle-row">
                  <span className="cor-settle-label">{t('customerOutstanding.invoiceDate')}</span>
                  <span className="cor-settle-value">{fmtDate(selectedInvoice.invoiceDate)}</span>
                </div>
                <div className="cor-settle-row">
                  <span className="cor-settle-label">{t('customerOutstanding.dueDate')}</span>
                  <span className={`cor-settle-value ${selectedInvoice.isOverdue ? 'cor-settle-overdue' : ''}`}>
                    {fmtDate(selectedInvoice.dueDate)}
                    {selectedInvoice.isOverdue && (
                      <span className="ims-badge ims-badge-rejected" style={{ marginInlineStart: '8px', fontSize: '10px' }}>
                        {t('customerOutstanding.overdue')}
                      </span>
                    )}
                  </span>
                </div>
                <div className="cor-settle-divider" />
                <div className="cor-settle-row cor-settle-balance-row">
                  <span className="cor-settle-label">{t('customerOutstanding.balanceDue')}</span>
                  <span className="cor-settle-balance">{fmt(selectedInvoice.balanceDue)}</span>
                </div>
              </div>

              {/* Error */}
              {settleError && (
                <div className="ims-alert ims-alert-error cor-settle-error">
                  <AlertCircle size={15} strokeWidth={2} />
                  {settleError}
                </div>
              )}

              {/* Settlement date */}
              <div className="cor-modal-field">
                <label className="cor-modal-label">
                  {t('customerOutstanding.settlementDate')}
                  <span className="cor-required"> *</span>
                </label>
                <input
                  type="date"
                  className="cor-modal-input"
                  value={settlementDate}
                  onChange={e => setSettlementDate(e.target.value)}
                  disabled={settling}
                />
              </div>

              {/* Remarks */}
              <div className="cor-modal-field">
                <label className="cor-modal-label">
                  {t('customerOutstanding.remarks')}
                  <span className="cor-optional"> ({t('common.optional') || 'optional'})</span>
                </label>
                <textarea
                  className="cor-modal-textarea"
                  value={remarks}
                  onChange={e => setRemarks(e.target.value)}
                  rows={3}
                  placeholder={t('common.enterRemarks') || 'Enter remarks…'}
                  disabled={settling}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="cor-modal-footer">
              <button className="ims-btn ims-btn-secondary" onClick={closeModal} disabled={settling}>
                {t('common.cancel')}
              </button>
              <button
                className="ims-btn ims-btn-success"
                onClick={handleSettle}
                disabled={settling || !settlementDate}
              >
                {settling ? (
                  <>
                    <span className="cor-spinner" />
                    {t('common.processing') || 'Processing…'}
                  </>
                ) : (
                  <>
                    <Check size={15} strokeWidth={2} />
                    {t('customerOutstanding.settleButton')}
                  </>
                )}
              </button>
            </div>

          </div>
        </div>
      )}
    </Layout>
  );
};

export default CustomerOutstandingReviewPage;
